import { useMemo, useState } from 'react';

import { ThirdPartyTypeEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { UseStepsHookResult } from '../../../components/Wizard/types/use-steps-hook-result.type';

import { ThirdPartyAddressesFormStep } from '../steps/Addresses';
import { ThirdPartyBankInfoFormStep } from '../steps/BankInfo';
import { ThirdPartyBillEmittersFormStep } from '../steps/BillEmitters';
import { ThirdPartyBillReceiverFormStep } from '../steps/BillReceiver';
import { ThirdPartyChildOrganizationsFormStep } from '../steps/ChildOrganizations';
import { ThirdPartyOrganizationMembershipsFormStep } from '../steps/OrganizationMemberships';
import { ThirdPartyOrganizationMembershipsDetailsFormStep } from '../steps/OrganizationMembershipsDetails';
import { ThirdPartyContactInfoFormStep } from '../steps/ContactInfo';
import { ThirdPartyOrganizationGeneralInfoFormStep } from '../steps/GeneralInfo/Organization';
import { ThirdPartyPersonGeneralInfoFormStep } from '../steps/GeneralInfo/Person';
import { ThirdPartyMotherOrganizationFormStep } from '../steps/MotherOrganization';
import { ThirdPartyPersonMembershipsFormStep } from '../steps/PersonMemberships';
import { ThirdPartyPersonMembershipsDetailsFormStep } from '../steps/PersonMembershipsDetails';
import { ThirdPartyFormTab } from '../enums/tab.enum';

interface Params {
  readonly isInternal: boolean;
  readonly kind: ThirdPartyTypeEnum;
  readonly stepIdsSubset?: readonly ThirdPartyFormTab[];
  readonly thirdPartyId?: Id;
}

export const useThirdPartySteps = <V extends object>({
  isInternal,
  kind,
  stepIdsSubset,
  thirdPartyId,
}: Readonly<Params>): UseStepsHookResult<V, ThirdPartyFormTab> => {
  const { addressableConfigsCodeMap } = useReferencesContext();

  const addressableConfig = isInternal
    ? kind === ThirdPartyTypeEnum.Organization
      ? addressableConfigsCodeMap.InternalOrganization
      : addressableConfigsCodeMap.InternalPerson
    : addressableConfigsCodeMap.ExternalThirdParty;

  const [hasMemberships, setHasMemberships] = useState(false);

  const { steps, wizardChildren } = useMemo(() => {
    const generalInfo = {
      step: {
        id: ThirdPartyFormTab.GeneralInfo,
        title: 'Informations Générales',
        isEnabled: true,
      },
      child:
        kind === ThirdPartyTypeEnum.Organization ? (
          <ThirdPartyOrganizationGeneralInfoFormStep
            id={ThirdPartyFormTab.GeneralInfo}
            key={ThirdPartyFormTab.GeneralInfo}
          />
        ) : (
          <ThirdPartyPersonGeneralInfoFormStep
            id={ThirdPartyFormTab.GeneralInfo}
            key={ThirdPartyFormTab.GeneralInfo}
          />
        ),
    };

    const contactInfo = {
      step: {
        id: ThirdPartyFormTab.ContactInfo,
        title: 'Contact',
        isEnabled: true,
      },
      child: (
        <ThirdPartyContactInfoFormStep
          id={ThirdPartyFormTab.ContactInfo}
          key={ThirdPartyFormTab.ContactInfo}
        />
      ),
    };

    const addresses = {
      step: {
        id: ThirdPartyFormTab.Addresses,
        title: 'Adresses',
        isEnabled: true,
      },
      child: (
        <ThirdPartyAddressesFormStep
          config={addressableConfig}
          id={ThirdPartyFormTab.Addresses}
          key={ThirdPartyFormTab.Addresses}
          useDirectMutation={false}
        />
      ),
    };

    const billEmitters = {
      step: {
        id: ThirdPartyFormTab.BillEmitters,
        title: 'Émetteurs de factures',
        isEnabled: true,
      },
      child: (
        <ThirdPartyBillEmittersFormStep
          id={ThirdPartyFormTab.BillEmitters}
          isInternal={isInternal}
          key={ThirdPartyFormTab.BillEmitters}
          thirdPartyId={thirdPartyId}
        />
      ),
    };

    const billReceiver = {
      step: {
        id: ThirdPartyFormTab.BillReceiver,
        title: 'Destinataire des factures',
        isEnabled: true,
      },
      child: (
        <ThirdPartyBillReceiverFormStep
          id={ThirdPartyFormTab.BillReceiver}
          isInternal={isInternal}
          key={ThirdPartyFormTab.BillReceiver}
          thirdPartyId={thirdPartyId}
        />
      ),
    };

    const bankInfo = {
      step: {
        id: ThirdPartyFormTab.BankInfo,
        title: 'Bancaire',
        isEnabled: true,
      },
      child: (
        <ThirdPartyBankInfoFormStep
          id={ThirdPartyFormTab.BankInfo}
          key={ThirdPartyFormTab.BankInfo}
        />
      ),
    };

    const organizationMemberships = {
      step: {
        id: ThirdPartyFormTab.OrganizationMemberships,
        title: 'Membres',
        isEnabled: true,
      },
      child: (
        <ThirdPartyOrganizationMembershipsFormStep
          id={ThirdPartyFormTab.OrganizationMemberships}
          isInternal={isInternal}
          key={ThirdPartyFormTab.OrganizationMemberships}
          setHasMemberships={setHasMemberships}
        />
      ),
    };

    const organizationMembershipsDetails = {
      step: {
        id: ThirdPartyFormTab.OrganizationMembershipsDetails,
        title: 'Détails Membres',
        isEnabled: hasMemberships,
      },
      child: (
        <ThirdPartyOrganizationMembershipsDetailsFormStep
          id={ThirdPartyFormTab.OrganizationMembershipsDetails}
          isInternal={isInternal}
          key={ThirdPartyFormTab.OrganizationMembershipsDetails}
        />
      ),
    };

    const personMemberships = {
      step: {
        id: ThirdPartyFormTab.PersonMemberships,
        title: 'Organisations',
        isEnabled: true,
      },
      child: (
        <ThirdPartyPersonMembershipsFormStep
          id={ThirdPartyFormTab.PersonMemberships}
          isInternal={isInternal}
          key={ThirdPartyFormTab.PersonMemberships}
          setHasMemberships={setHasMemberships}
        />
      ),
    };

    const personMembershipsDetails = {
      step: {
        id: ThirdPartyFormTab.PersonMembershipsDetails,
        title: 'Détails Membres',
        isEnabled: hasMemberships,
      },
      child: (
        <ThirdPartyPersonMembershipsDetailsFormStep
          id={ThirdPartyFormTab.PersonMembershipsDetails}
          isInternal={isInternal}
          key={ThirdPartyFormTab.PersonMembershipsDetails}
        />
      ),
    };

    const childOrganizations = {
      step: {
        id: ThirdPartyFormTab.ChildOrganizations,
        title: 'Organisations Filles',
        isEnabled: true,
      },
      child: (
        <ThirdPartyChildOrganizationsFormStep
          id={ThirdPartyFormTab.ChildOrganizations}
          isInternal={isInternal}
          key={ThirdPartyFormTab.ChildOrganizations}
          organizationId={thirdPartyId}
        />
      ),
    };

    const motherOrganization = {
      step: {
        id: ThirdPartyFormTab.MotherOrganization,
        title: 'Organisation Mère',
        isEnabled: true,
      },
      child: (
        <ThirdPartyMotherOrganizationFormStep
          id={ThirdPartyFormTab.MotherOrganization}
          isInternal={isInternal}
          key={ThirdPartyFormTab.MotherOrganization}
          organizationId={thirdPartyId}
        />
      ),
    };

    const allTabs = [
      [ThirdPartyFormTab.GeneralInfo, generalInfo],
      [ThirdPartyFormTab.ContactInfo, contactInfo],
      [ThirdPartyFormTab.Addresses, addresses],
      [ThirdPartyFormTab.BillEmitters, billEmitters],
      [ThirdPartyFormTab.BillReceiver, billReceiver],
      [ThirdPartyFormTab.BankInfo, bankInfo],
      ...(kind === ThirdPartyTypeEnum.Organization
        ? ([
            [
              ThirdPartyFormTab.OrganizationMemberships,
              organizationMemberships,
            ],
            [
              ThirdPartyFormTab.OrganizationMembershipsDetails,
              organizationMembershipsDetails,
            ],
            [ThirdPartyFormTab.ChildOrganizations, childOrganizations],
            [ThirdPartyFormTab.MotherOrganization, motherOrganization],
          ] as const)
        : ([
            [ThirdPartyFormTab.PersonMemberships, personMemberships],
            [
              ThirdPartyFormTab.PersonMembershipsDetails,
              personMembershipsDetails,
            ],
          ] as const)),
    ] as const;

    const activeTabs = stepIdsSubset?.length
      ? allTabs.filter(([tab]) => stepIdsSubset.includes(tab))
      : allTabs;

    const steps = activeTabs.map(([, { step }]) => step);
    const wizardChildren = activeTabs.map(([, { child }]) => child);

    return { steps, wizardChildren };
  }, [
    addressableConfig,
    hasMemberships,
    isInternal,
    kind,
    stepIdsSubset,
    thirdPartyId,
  ]);

  return { steps, wizardChildren };
};
